import moment from 'moment-timezone';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { BehaviorSubject, concat, merge, Observable, of, repeat, timer } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, tap, toArray } from 'rxjs/operators';
import { isDefined } from '../utils';
import {
  HappyHoursPeriodModel,
  HappyHoursPeriodNextModel,
  HappyHoursPeriodNowModel,
  IHappyHoursPeriod,
  IHappyHoursPeriodRaw,
} from '../models';
import { AuthService } from './auth.service';
import { WatchdogService } from './watchdog.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WebsocketService } from './websocket.service';
import { HappyHoursRepository } from '../repositories/happy-hours.repository';
import { ClusterService } from './cluster.service';
import { Moment } from 'moment';

const weekInSeconds = 604_800_000;

@Injectable()
export class HappyHoursService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly auth = inject(AuthService);
  private readonly watchdog = inject(WatchdogService);
  private readonly cluster = inject(ClusterService);
  private readonly hhRepository = inject(HappyHoursRepository);
  private readonly webSocket = inject(WebsocketService);


  private readonly logger = this.watchdog.tag('Happy Hours', 'green');

  public readonly happyHours$ = this.hhRepository.all$().pipe(
    map((entries) => entries.map((entry) => new HappyHoursPeriodModel(entry))),
    tap({
      next: (entries) => this.logger.debug('Happy hours all loaded', entries),
      error: (error) => this.logger.error('Failed to load all happy hours', error),
    }),
  );

  public readonly status$ = new BehaviorSubject<HappyHoursPeriodNextModel | HappyHoursPeriodNowModel | null>(null);
  public readonly isNow$ = this.status$.pipe(
    map((hh) => hh instanceof HappyHoursPeriodNowModel),
    distinctUntilChanged(),
  );

  public initialize(): void {
    this.logger.info('Initialized');

    this.cluster.leader$.pipe(
      filter((leader) => !!leader),
      switchMap(() => this.auth.logouted$),
      switchMap(() => this.hhRepository.clear$()),
      tap(() => this.logger.info('Cleared happy hours on logout')),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    this.cluster.leader$.pipe(
      filter((leader) => !!leader),
      switchMap(() => this.webSocket.messages$),
      filter((response) => response.type === 'tableInfo'),
      switchMap((response) => {
        if (response.data.happyMenuPeriods) {
          return this.updateByCollection(response.data.happyMenuPeriods);
        } else {
          return this.hhRepository.clear$();
        }
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    this.happyHours$.pipe(
      switchMap((periods) => {
        if (periods.length === 0) {
          return of(null);
        }

        return this.watchHappyHours(periods);
      }),
      filter((status) => status !== null),
      distinctUntilChanged((prev, curr) => {
        return prev?.constructor === curr?.constructor &&
          JSON.stringify(prev) === JSON.stringify(curr);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((status) => {
      this.logger.debug('Happy hours status:', status);
      this.status$.next(status);
    });
  }

  public updateByCollection(timetable: IHappyHoursPeriodRaw[]): Observable<IHappyHoursPeriod[]> {
    return this.happyHours$.pipe(
      take(1),
      filter((value) => {
        const newValue = timetable.map(HappyHoursPeriodModel.parseRaw);

        return JSON.stringify(value) !== JSON.stringify(newValue);
      }),
      switchMap(() => this.hhRepository.clear$()),
      switchMap(() =>
        concat(
          ...timetable.map((hh) =>
            this.hhRepository.add$(HappyHoursPeriodModel.parseRaw(hh)).pipe(
              filter(isDefined),
            ),
          ),
        ).pipe(
          toArray(),
        ),
      ),
    );
  }

  private watchHappyHours(periods: IHappyHoursPeriod[]): Observable<HappyHoursPeriodNextModel | HappyHoursPeriodNowModel | null> {
    const now = moment();
    const next = this.findNextPeriod(periods, now);

    const timers = periods.map((period) => {
      const start = this.getPeriodStart(period, now);
      const end = this.getPeriodEnd(period, now);

      const starTimer = (startDue: Date) => timer(startDue, weekInSeconds).pipe(
        map(() => new HappyHoursPeriodNowModel(period)),
      );

      const endTimer = (startDue: Date) => timer(startDue, weekInSeconds).pipe(
        map(() => {
          const nextPeriod = this.findNextPeriod(periods, moment());

          return nextPeriod ? new HappyHoursPeriodNextModel(nextPeriod) : null;
        })
      );

      if (now.isBetween(start, end)) {
        return [
          of(new HappyHoursPeriodNowModel(period)),
          starTimer(start.add('1', 'weeks').toDate()),
          endTimer(end.toDate()),
        ];
      }

      if (end.isBefore(now)) {
        start.add('1', 'weeks');
        end.add('1', 'weeks');
      }

      return [
        starTimer(start.toDate()),
        endTimer(end.toDate())
      ];
    }).flat();

    if (timers.length === 0) {
      return of(null);
    }

    return merge(of(next ? new HappyHoursPeriodNextModel(next) : null),  ...timers);
  }

  private findNextPeriod(periods: IHappyHoursPeriod[], fromTime: Moment): IHappyHoursPeriod | null {
    const models = periods.map((p) => new HappyHoursPeriodModel(p));

    const next = models
      .map((model) => {
        const start = this.getPeriodStart(model, fromTime);

        if (!fromTime.isBefore(start)) {
          start.add(1, 'weeks');
        }

        return { model, start };
      })
      .sort((a, b) => {
        return a.start.diff(b.start);
      })[0];

    return next?.model ?? null;
  }

  private getPeriodStart(period: IHappyHoursPeriod, fromTime: Moment): Moment {
    return fromTime.clone().set({
      isoWeekday: period.startDay,
      hour: period.startTime.hours,
      minute: period.startTime.minutes,
      second: 0,
      millisecond: 0,
    });
  }

  private getPeriodEnd(period: IHappyHoursPeriod, fromTime: Moment): Moment {
    const start = this.getPeriodStart(period, fromTime);
    const end = fromTime.clone().set({
      isoWeekday: period.endDay,
      hour: period.endTime.hours,
      minute: period.endTime.minutes,
      second: 0,
      millisecond: 0,
    });

    if (!end.isAfter(start)) {
      end.add(1, 'weeks');
    }

    return end;
  }

}
